"use client";

import { BarChart, type BarChartData } from "@/shared/ui/BarChart";
import { Card, CardContent } from "@/components/ui/card";
import { useMemo } from "react";

export type DashboardChartsProps = {
  salesOverTimeData?: BarChartData[];
  salesByBranchData?: BarChartData[];
  ordersByStatusData?: BarChartData[];
  isLoading?: boolean;
};

export function DashboardCharts({
  salesOverTimeData,
  salesByBranchData,
  ordersByStatusData,
  isLoading,
}: DashboardChartsProps) {
  const salesOverTimeConfig = useMemo(
    () => ({
      sales: {
        label: "ຍອດຂາຍ (LAK)",
        color: "var(--chart-1)",
      },
    }),
    [],
  );

  const salesByBranchConfig = useMemo(
    () => ({
      sales: {
        label: "ຍອດຂາຍ (LAK)",
        color: "var(--chart-2)",
      },
    }),
    [],
  );

  const ordersByStatusConfig = useMemo(
    () => ({
      pending: {
        label: "ລໍຖ້າ",
        color: "var(--chart-3)",
      },
      processing: {
        label: "ກຳລັງດຳເນີນການ",
        color: "var(--chart-4)",
      },
      completed: {
        label: "ສຳເລັດ",
        color: "var(--chart-5)",
      },
      cancelled: {
        label: "ຍົກເລີກ",
        color: "var(--destructive)",
      },
    }),
    [],
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="p-6">
              <div className="mb-4 h-6 w-32 animate-pulse rounded bg-muted" />
              <div className="h-64 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
      {/* Sales Over Time Chart */}
      {salesOverTimeData && salesOverTimeData.length > 0 && (
        <Card className="rounded-xl border bg-card">
          <CardContent className="">
            <h3 className="mb-4 font-semibold text-lg">ຍອດຂາຍຕາມເວລາ</h3>
            <div className="h-64 w-full">
              <BarChart
                data={salesOverTimeData}
                config={salesOverTimeConfig}
                xDataKey="period"
                valueDataKey="sales"
                className="h-full w-full"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales By Branch Chart */}
      {salesByBranchData && salesByBranchData.length > 0 && (
        <Card className="rounded-xl border bg-card">
          <CardContent className="">
            <h3 className="mb-4 font-semibold text-lg">ຍອດຂາຍຕາມສາຂາ</h3>
            <div className="h-64 w-full">
              <BarChart
                data={salesByBranchData}
                config={salesByBranchConfig}
                xDataKey="branch"
                valueDataKey="sales"
                className="h-full w-full"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders By Status Chart */}
      {ordersByStatusData && ordersByStatusData.length > 0 && (
        <Card className="rounded-xl border bg-card sm:col-span-1 lg:col-span-2">
          <CardContent className="">
            <h3 className="mb-4 font-semibold text-lg">ຈຳນວນອໍເດີຕາມສະຖານະ</h3>
            <div className="h-64 w-full">
              <BarChart
                data={ordersByStatusData}
                config={ordersByStatusConfig}
                xDataKey="status"
                valueDataKey={[
                  "pending",
                  "processing",
                  "completed",
                  "cancelled",
                ]}
                className="h-full w-full"
                stacked
              />
            </div>
          </CardContent>
        </Card>
      )}

      {!salesOverTimeData &&
        !salesByBranchData &&
        !ordersByStatusData &&
        !isLoading && (
          <div className="col-span-full flex h-64 items-center justify-center text-muted-foreground">
            ບໍ່ມີຂໍ້ມູນ
          </div>
        )}
    </div>
  );
}
